import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { Popover } from 'antd';
import dayjs from 'dayjs';
import {
  FiFilter,
  FiDownload,
  FiFolder,
  FiGrid,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiCheckSquare,
  FiMoreVertical,
  FiEye,
} from 'react-icons/fi';

import firebase from '../../config/firebase.config';
import { regularizationActionTypes } from '@store/Regularization/Regularization.actionTypes';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import {
  getRegularizationReporterLabel,
  REG_STATUS_TONE,
} from '../Regulation/Regularization.helper';
import { toDateValue } from '@/utils/firestoreDate';
import { getHashedColor } from '@/utils/colorHash';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_MANAGER_REGULARIZATION_REQUESTS,
  DEMO_TEAMS_PROJECTS,
  DEMO_MEMBER_DETAILS,
} from './RegularizationRequests.demoData';

import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { StatCard } from '@/components/StatCard';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import { InitialsBadge } from '@/components/InitialsBadge';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { DateRangePicker } from '@/components/DateRangePicker';
import { Button, buttonVariants } from '@/components/Button';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { ActionsMenu } from '@components/ActionsMenu';
import { addNotification, notificationStates } from '@components/Notification';

import { ViewRegularizationModal } from '../Regulation/ViewRegularizationModal.component';
import styles from './RegularizationRequests.module.scss';

const database = firebase.firestore();
const PAGE_SIZE = 10;

const REG_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All Status' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Declined', label: 'Declined' },
];

const STAT_CARDS_CONFIG = [
  {
    key: 'total',
    label: 'Total Requests',
    caption: 'All time',
    icon: <FiGrid />,
    iconBg: 'var(--purple-100)',
  },
  {
    key: 'pending',
    label: 'Pending',
    caption: 'Awaiting action',
    icon: <FiClock />,
    iconBg: 'var(--warning-100)',
  },
  {
    key: 'approved',
    label: 'Approved',
    caption: 'This month',
    icon: <FiCheckCircle />,
    iconBg: 'var(--success-100)',
  },
  {
    key: 'declined',
    label: 'Declined',
    caption: 'This month',
    icon: <FiXCircle />,
    iconBg: 'var(--error-100)',
  },
  {
    key: 'completed',
    label: 'Completed',
    caption: 'This month',
    icon: <FiCheckSquare />,
    iconBg: 'var(--blue-100)',
  },
];

function PlainCell({ children }) {
  return (
    <Text variant={textVariantsEnum.Text_sm} fontWeight={fontWeightEnum.Normal}>
      {children}
    </Text>
  );
}

function DateCell({ date }) {
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        ({date.toLocaleDateString(undefined, { weekday: 'short' })})
      </Text>
    </div>
  );
}

function ReportDateCell({ date }) {
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        {date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Text>
    </div>
  );
}

function ProjectCell({ projectName }) {
  const color = getHashedColor(projectName || 'project');
  return (
    <div className={styles.departmentCell}>
      <span
        className={styles.departmentIconWrap}
        style={{ backgroundColor: `var(--${color}-100)` }}
      >
        <FiFolder size={14} color={`var(--${color}-700)`} />
      </span>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {projectName || 'N/A'}
      </Text>
    </div>
  );
}

function ReporterCell({ label, member }) {
  if (!label) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={label} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {label}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {member?.userType || '—'}
        </Text>
      </div>
    </div>
  );
}

export const RegularizationRequestsComponent = () => {
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const regularizationState = useSelector(
    (state) => state.regularization || {}
  );
  const dispatch = useDispatch();

  const [regList, setRegList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewRequest, setReviewRequest] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const menuRef = useRef(null);
  const [companyMemberDetails, setCompanyMemberDetails] = useState({});

  const [dateRange, setDateRange] = useState(() => [
    dayjs().startOf('month').toDate(),
    dayjs().endOf('month').toDate(),
  ]);
  const [statusFilter, setStatusFilter] = useState(
    REG_STATUS_FILTER_OPTIONS[0]
  );
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Demo mode: no real backend data exists for the demo profile, so
  // GET_MANAGER_REQUESTS/GET_PROJECT and the team-member Firestore lookup
  // below all resolve empty — override at the point of use so the page
  // shows a realistic requests queue instead of "No Requests Found"
  // everywhere. Real dispatches/state above keep running unchanged.
  const displayRegList = IS_DEMO_MODE
    ? DEMO_MANAGER_REGULARIZATION_REQUESTS
    : regList;
  const displayTeams = IS_DEMO_MODE
    ? { ...teams, projects: DEMO_TEAMS_PROJECTS }
    : teams;
  const displayMemberDetails = IS_DEMO_MODE
    ? DEMO_MEMBER_DETAILS
    : companyMemberDetails;
  const displayLoading = IS_DEMO_MODE ? false : loading;

  const teamProjects = useMemo(
    () =>
      Array.isArray(displayTeams.projects)
        ? displayTeams.projects.filter((item) => !item.data?.deleted)
        : [],
    [displayTeams.projects]
  );

  const getRegData = () => {
    setLoading(true);
    dispatch({
      type: regularizationActionTypes.GET_MANAGER_REQUESTS,
      payload: {
        managerId: profile.id,
        companyId: activeCompany.companyId,
      },
    });
  };

  // Load member details for all teams in company (requests can reference any
  // team's members) — mirrors LeaveRequest.component.jsx's aggregation.
  useEffect(() => {
    // Demo mode: no real teamDatabase docs exist for the demo company, so
    // this lookup would just resolve empty. Skip it — displayMemberDetails
    // is already seeded with DEMO_MEMBER_DETAILS below.
    if (IS_DEMO_MODE) return;
    if (!activeCompany?.companyId) return;
    database
      .collection('teamDatabase')
      .where('ownedBy', '==', activeCompany.companyId)
      .get()
      .then(async (snap) => {
        const teamIds = snap.docs.map((d) => d.id);
        const merged = {};
        await Promise.all(
          teamIds.map(async (teamId) => {
            const details = await getTeamMemberDetails(database, teamId);
            if (details && typeof details === 'object') {
              Object.assign(merged, details);
            }
          })
        );
        setCompanyMemberDetails(merged);
      })
      .catch(() => setCompanyMemberDetails({}));
  }, [activeCompany?.companyId]);

  const handleApprove = async () => {
    if (!reviewRequest || approving || declining) return;
    if (IS_DEMO_MODE) {
      addNotification({
        message: 'Approving requests is not available in demo mode.',
        level: notificationStates.info,
      });
      setReviewRequest(null);
      return;
    }
    setApproving(true);
    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.APPROVE_REQUEST,
        payload: {
          requestData: reviewRequest,
          companyId: activeCompany.companyId,
          onComplete: resolve,
        },
      });
    });
    setApproving(false);
    if (result?.success) {
      setReviewRequest(null);
      getRegData();
    }
  };

  const handleDecline = async () => {
    if (!reviewRequest || approving || declining) return;
    if (IS_DEMO_MODE) {
      addNotification({
        message: 'Declining requests is not available in demo mode.',
        level: notificationStates.info,
      });
      setReviewRequest(null);
      return;
    }
    setDeclining(true);
    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.DECLINE_REQUEST,
        payload: {
          requestId: reviewRequest?.id,
          companyId: activeCompany.companyId,
          assigneeId: reviewRequest?.assignee,
          onComplete: resolve,
        },
      });
    });
    setDeclining(false);
    if (result?.success) {
      setReviewRequest(null);
      getRegData();
    }
  };

  const onActionsButtonClick = (e, rowData) => {
    setActiveMenuRow(rowData);
    menuRef.current.toggle(e);
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    return [
      {
        label: 'View',
        icon: <FiEye size={16} />,
        onClick: () => {
          setViewRequest(activeMenuRow);
          menuRef.current?.hide();
        },
      },
    ];
  }, [activeMenuRow]);

  useEffect(() => {
    if (regularizationState.managerRequests) {
      setRegList(regularizationState.managerRequests);
      setLoading(regularizationState.loading || false);
    }
  }, [regularizationState.managerRequests, regularizationState.loading]);

  useEffect(() => {
    if (!isEmpty(teams?.active) && activeCompany?.companyId) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
      getRegData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.companyId]);

  const departmentFilterOptions = useMemo(() => {
    const names = [
      ...new Set(displayRegList.map((r) => r.teamName).filter(Boolean)),
    ];
    return [
      { value: null, label: 'All Departments' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [displayRegList]);

  const dateFilteredRows = useMemo(() => {
    const [start, end] = dateRange || [];
    if (!start || !end) return displayRegList;
    return displayRegList.filter((row) => {
      const d = toDateValue(row.startTime);
      return Boolean(d) && d >= start && d <= end;
    });
  }, [displayRegList, dateRange]);

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter((row) => {
      if (statusFilter?.value && row.status !== statusFilter.value)
        return false;
      if (departmentFilter?.value && row.teamName !== departmentFilter.value)
        return false;
      return true;
    });
  }, [dateFilteredRows, statusFilter, departmentFilter]);

  const sortedRows = useMemo(() => {
    if (!sortedBy) return filteredRows;
    const getTime = (row) => {
      const date =
        sortedBy === 'taskDate'
          ? toDateValue(row.startTime)
          : toDateValue(row.createdAt || row.requestDate);
      return date ? date.getTime() : 0;
    };
    const sorted = [...filteredRows].sort((a, b) => getTime(a) - getTime(b));
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredRows, sortedBy, reverseSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, statusFilter, departmentFilter, sortedBy, reverseSort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const stats = useMemo(() => {
    const now = new Date();
    const isThisMonth = (row) => {
      const d = toDateValue(row.createdAt || row.requestDate);
      return (
        Boolean(d) &&
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth()
      );
    };
    const approved = displayRegList.filter(
      (r) => r.status === 'Approved' && isThisMonth(r)
    ).length;
    const declined = displayRegList.filter(
      (r) => r.status === 'Declined' && isThisMonth(r)
    ).length;
    return {
      total: displayRegList.length,
      pending: displayRegList.filter((r) => r.status === 'Pending').length,
      approved,
      declined,
      completed: approved + declined,
    };
  }, [displayRegList]);

  const exportToCSV = () => {
    if (sortedRows.length === 0) {
      addNotification({
        message: 'No data to export for the selected filters.',
        level: notificationStates.warning,
      });
      return;
    }
    const headers = [
      'ID',
      'Project',
      'Task Name',
      'Duration',
      'Task Date',
      'Department',
      'Report Date',
      'Reported By',
      'Status',
    ];
    const rows = sortedRows.map((row, index) => {
      const project = teamProjects.find((p) => p.id === row.projectId);
      const taskDate = toDateValue(row.startTime);
      const reportDate = toDateValue(row.createdAt || row.requestDate);
      return [
        escapeCSVField(index + 1),
        escapeCSVField(project?.data?.project_name),
        escapeCSVField(row.taskName),
        escapeCSVField(`${row.taskHours || 0}h ${row.taskMinutes || 0}m`),
        escapeCSVField(taskDate ? taskDate.toLocaleDateString() : ''),
        escapeCSVField(row.teamName),
        escapeCSVField(reportDate ? reportDate.toLocaleDateString() : ''),
        escapeCSVField(
          getRegularizationReporterLabel(row, displayMemberDetails)
        ),
        escapeCSVField(row.status),
      ].join(',');
    });
    const date = new Date().toISOString().split('T')[0];
    downloadCSV({
      headers,
      rows,
      filename: `regularization-requests-team-${date}.csv`,
    });
    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const clearFilters = () => {
    setStatusFilter(REG_STATUS_FILTER_OPTIONS[0]);
    setDepartmentFilter(null);
  };

  const filterPopoverContent = (
    <div className={styles.filterPopover}>
      <Dropdown
        label="Status"
        options={REG_STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Dropdown
        label="Department"
        options={departmentFilterOptions}
        value={departmentFilter}
        onChange={setDepartmentFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Button
        text="Clear filters"
        variant={buttonVariants.ghost}
        onClick={clearFilters}
      />
    </div>
  );

  const columns = [
    {
      id: 'id',
      name: 'ID',
      style: { width: '3%' },
      cellRenderer: (row, rowIndex) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {(currentPage - 1) * PAGE_SIZE + rowIndex + 1}
        </Text>
      ),
    },
    {
      id: 'project',
      name: 'Project / Task',
      style: { width: '20%' },
      cellRenderer: (row) => {
        const project = teamProjects.find((p) => p.id === row.projectId);
        return <ProjectCell projectName={project?.data?.project_name} />;
      },
    },
    {
      id: 'duration',
      name: 'Duration',
      style: { width: '10%' },
      cellRenderer: (row) => (
        <PlainCell>
          {row.taskHours || 0}h {String(row.taskMinutes || 0).padStart(2, '0')}m
        </PlainCell>
      ),
    },
    {
      id: 'taskDate',
      name: 'Task Date',
      sortable: true,
      sortKey: 'taskDate',
      style: { width: '14%' },
      cellRenderer: (row) => <DateCell date={toDateValue(row.startTime)} />,
    },
    {
      id: 'reportDate',
      name: 'Report Date',
      sortable: true,
      sortKey: 'reportDate',
      style: { width: '14%' },
      cellRenderer: (row) => (
        <ReportDateCell date={toDateValue(row.createdAt || row.requestDate)} />
      ),
    },
    {
      id: 'reporter',
      name: 'Reported By',
      style: { width: '16%' },
      cellRenderer: (row) => (
        <ReporterCell
          label={getRegularizationReporterLabel(row, displayMemberDetails)}
          member={displayMemberDetails?.[row.userId]}
        />
      ),
    },
    {
      id: 'status',
      name: 'Status',
      style: { width: '9%' },
      cellRenderer: (row) => (
        <StatusBadge
          label={row.status}
          tone={REG_STATUS_TONE[row.status] || statusBadgeTones.default}
          dot
        />
      ),
    },
    {
      id: 'action',
      name: 'Action',
      style: { width: '14%' },
      cellRenderer: (row) => (
        <div className={styles.actionsCell}>
          {row.status === 'Pending' && (
            <Button
              variant={buttonVariants.primary_rounded}
              size="sm"
              text="Review"
              onClick={() => setReviewRequest(row)}
            />
          )}
          <Button
            icon={<FiMoreVertical size={14} />}
            variant={buttonVariants.outline}
            ariaLabel="Actions"
            onClick={(e) => onActionsButtonClick(e, row)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={styles.regularizationRequestsPage}>
        <div className={styles.regularizationRequestsContainer}>
          <div className={styles.regularizationRequestsHeader}>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <div className={styles.regularizationRequestsHeaderActions}>
              <Popover
                content={filterPopoverContent}
                trigger="click"
                placement="bottomRight"
              >
                <Button
                  text="Filter"
                  icon={<FiFilter size={14} />}
                  variant={buttonVariants.outline}
                />
              </Popover>
              <Button
                text="Export"
                icon={<FiDownload size={14} />}
                variant={buttonVariants.outline}
                onClick={exportToCSV}
              />
            </div>
          </div>

          <div className={styles.statsRow}>
            {STAT_CARDS_CONFIG.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                iconBg={card.iconBg}
                label={card.label}
                caption={card.caption}
                value={stats[card.key]}
              />
            ))}
          </div>

          <div className={styles.regularizationRequestsTableContainer}>
            {displayLoading ? (
              <div className="w-100 text-center py-5">
                <Spinner />
              </div>
            ) : sortedRows.length === 0 ? (
              <EmptyState
                illustration
                title="No Requests Found"
                description="No regularization requests match the selected filters."
              />
            ) : (
              <>
                <Table
                  className={styles.regularizationRequestsTable}
                  data={pagedRows}
                  columns={columns}
                  onSortClick={handleSortClick}
                  sortedBy={sortedBy}
                  reverseSort={reverseSort}
                />
                <div className={styles.tableFooter}>
                  <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(currentPage * PAGE_SIZE, sortedRows.length)} of{' '}
                    {sortedRows.length} requests
                  </Text>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ViewRegularizationModal
        isVisible={Boolean(reviewRequest)}
        rowData={reviewRequest}
        teamProjects={teamProjects}
        memberDetails={displayMemberDetails}
        title="Process Regularization Request"
        onApprove={handleApprove}
        onDecline={handleDecline}
        approveLoading={approving}
        declineLoading={declining}
        onClose={() => setReviewRequest(null)}
      />
      <ViewRegularizationModal
        isVisible={Boolean(viewRequest)}
        rowData={viewRequest}
        teamProjects={teamProjects}
        memberDetails={displayMemberDetails}
        onClose={() => setViewRequest(null)}
      />
      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
    </>
  );
};

RegularizationRequestsComponent.displayName = 'RegularizationRequests';
